import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, model, OnInit, Renderer2 } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { DataResult, ICRUDOptions } from '@phx-erp/shared';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom } from "rxjs";
import { GroupedItems } from '../../../../../@core/interfaces/ISelectGroupInterfaces';
import { NotificationService } from '../../../../../@core/services/notification/notification.service';
import { Address, AddressPostalLink, AddressPostalLinkSearchInput, AddressPostalLinkWithPrimaryPostalAddressView, DocumentDirection } from '../../../../../generated/generated-types';
import { GenericBaseModelDropDown } from '../../../../../shared/components/genericBaseModelDropDown';
import { AddressPostalLinkService } from '../../../../crm/services/address-postal-link.service';

/**
 * this is for grouping and the extension is just to have type safety in GenericBaseModelDropDown
 */

@Component({
  selector: 'app-document-delivery-postal-address-dropdown',
  templateUrl: './document-delivery-postal-address-dropdown.component.html',
  styleUrls: ['./document-delivery-postal-address-dropdown.component.scss'],
  providers: [AddressPostalLinkService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TooltipModule,
    TranslatePipe,
  ]
})
export class DocumentDeliveryPostalAddressDropdownComponent extends GenericBaseModelDropDown<AddressPostalLink> implements OnInit {

  @Input() addressId: string;
  @Input() selectedDeliveryAddress: Address;

  @Input()
  showOnlyDebitors;

  @Input()
  showOnlyCreditors;

  showSelection: boolean = false;

  direction = model<DocumentDirection>();
  DocumentDirection = DocumentDirection;

  constructor(public service: AddressPostalLinkService,
    private renderer: Renderer2,
    private notification: NotificationService) {
    super(service, "AddressPostalLink");
  }

  async ngOnInit(): Promise<void> {
    this.enableGrouping.set(true);

    if (this.props?.addressId?.length > 0)
      this.addressId = this.props?.addressId;
    if (this.props?.showSelection === true)
      this.showSelection = true;


    this.value.subscribe((x: AddressPostalLink) => {
      if ((this.direction() === DocumentDirection.IN && x?.address?.documentBlockIn) || (this.direction() === DocumentDirection.OUT && x?.address?.documentBlockOut)) {
        if ((this.direction() === DocumentDirection.IN && x?.address?.documentBlockIn) && (this.direction() === DocumentDirection.OUT && x?.address?.documentBlockOut))
          this.notification.error(this.i18n.translate(_('crm.has-document-block')));
        if ((this.direction() === DocumentDirection.IN && x?.address?.documentBlockIn))
          this.notification.error(this.i18n.translate(_('crm.has-document-block-in')));
        if ((this.direction() === DocumentDirection.OUT && x?.address?.documentBlockOut))
          this.notification.error(this.i18n.translate(_('crm.has-document-block-out')));
        this.clear();
      }
    })

    super.ngOnInit();
  }

  async ngOnChanges(change: any): Promise<void> {
    if ((change?.showOnlyDebitors?.previousValue !== undefined && change?.showOnlyDebitors?.currentValue != change?.showOnlyDebitors?.previousValue) ||
      (change?.showOnlyCreditors?.previousValue !== undefined && change?.showOnlyCreditors?.currentValue != change?.showOnlyCreditors?.previousValue)) {
      this.refresh();
    }
  }

  override async executeSearch(searchInput?: AddressPostalLinkSearchInput, options?: ICRUDOptions) {
    if (this.addressId) {
      searchInput.addressIdFirst = this.addressId;
      if (this.showOnlyCreditors !== null && this.showOnlyCreditors !== undefined)
        searchInput.onlyOfCreditors = this.showOnlyCreditors;
      if (this.showOnlyDebitors !== null && this.showOnlyDebitors !== undefined)
        searchInput.onlyOfDebitors = this.showOnlyDebitors;
      searchInput.includeProspects = true;
    }

    //copy of super.executeSearch (except the service call)
    if (!options)
      options = { fetchPolicy: 'network-only' };
    else
      options.fetchPolicy = 'network-only';

    let res = await lastValueFrom(await this.service.getDataWithPrimaryPostalAddress(searchInput, options));
    return res as DataResult<AddressPostalLinkWithPrimaryPostalAddressView>;
  }


  //filter out any Items with addressId
  filterItemsByAddressId(items: AddressPostalLink[]): AddressPostalLink[] {
    let filteredItems = items?.filter(item => {
      return item.addressId != this.addressId;
    });
    return filteredItems;
  }

  override groupItems(value: AddressPostalLink[]): GroupedItems<AddressPostalLink>[] {
    return super.groupItems(value, {
      groupingLabelFunction: (item: AddressPostalLink) => {
        if (!item.address)
          return '';
        else {
          const identifierPart = ((item.address?.debitor?.identifier && item.address?.creditor?.identifier) ?
            `${item.address?.debitor?.identifier}/${item.address?.creditor?.identifier}` :
            (item.address?.debitor?.identifier || item.address?.creditor?.identifier));

          const namePart = item.address?.name || `${item.address?.firstName} ${item.address?.lastName}`;

          return `${identifierPart} - ${namePart}`;
        }
      },
      groupItemsSortFunction: (a, b) => ((b as any).isPrimary ? 2 : b.isDefault ? 1 : 0) - ((a as any).isPrimary ? 2 : a.isDefault ? 1 : 0)
    });
  }

  highlightItemCheck(item: AddressPostalLink, element: HTMLElement): boolean {
    let itemAddressId = item.addressId ?? item?.addressId;
    if (itemAddressId == this.addressId) {
      let highlightItem = element;
      if (highlightItem) {
        const parent = highlightItem.parentElement;
        if (parent && parent.previousElementSibling && parent.previousElementSibling.classList.contains('ng-optgroup')) {
          this.renderer.setStyle(parent.previousElementSibling, 'font-weight', 'bolder');
        }
      }
      return true;
    }
    return false;
  }

  isBlocked() {
    let item = this.selectedDeliveryAddress;
    if ((this.direction() === DocumentDirection.IN && item?.documentBlockIn) || (this.direction() === DocumentDirection.OUT && item?.documentBlockOut))
      return true;
    return false;
  }

  generateLabel(item?: AddressPostalLink) {

    let selectedItem = item;

    if (!selectedItem)
      selectedItem = { address: this.selectedDeliveryAddress } as AddressPostalLink;

    if (typeof selectedItem === 'string') {
      return ''
    }

    // let name = selectedItem?.name || '';

    // if (selectedItem?.firstName)
    //   name += ` ${selectedItem?.firstName}`;
    // if (selectedItem?.lastName)
    //   name += ` ${selectedItem?.lastName}`;

    // if (this.showOnlyCreditors && selectedItem?.creditor?.identifier)
    //   name = `${selectedItem?.creditor?.identifier} ${name}`;

    // if (this.showOnlyDebitors && selectedItem?.debitor?.identifier)
    //   name = `${selectedItem?.debitor?.identifier} ${name}`;

    // if (!this.showOnlyCreditors && !this.showOnlyDebitors)
    //   name = `${name}`;

    // if (name === '') {
    //   if (item?.defaultEmail) {
    //     name = `Address: Undefined, Email: ${item?.defaultEmail}`
    //   } else if (item?.email2) {
    //     name = `Address: Undefined, Email: ${item?.email2}`
    //   }
    // }

    // //  if none set as unknown
    // if (name === '') {
    //   name = 'Address: Undefined';
    // }

    let name = '';

    if (selectedItem?.postalAddress?.recipient)
      name = selectedItem?.postalAddress?.recipient;

    if (!name)
      name = this.i18n.translate(_('postal-address.missing-receiver'));

    return name;
  }

}
